import React from 'react';
import { CheckCircle, MapPin, Building, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const WestQurnaPhase2Page = () => {
  const infrastructureFacilities = [
    '33/11kV Substation Design Development',
    'Transformers, UPS & Switchgear Sizing Calculation',
    'Power System Studies in ETAP',
    'Fire & Gas System Design',
    'Integration of Power, Instrumentation and Control Systems, Security and Telecom systems with existing facilities'
  ];

  const facilitiesDesign = [
    'Oil Wells Clusters Design Development',
    'Security Facilities Design Development',
    'Auxiliary Piping & Plumbing works',
    'Access Roads & Bridge Design',
    'Oil Police Entry Check Point Design',
    'Telecom & Security System Design'
  ];

  const historicalWells = [
    'New Warehouse',
    'Office Buildings'
  ];

  const technicalWriting = [
    'Engineering Procedures such as Pressure Leak Test for Piping System, PWHT, HSE etc.',
    'Inspection & Test Plans (ITPs) such as Piping, Electrical Works, Earthworks, Steel Structure Works, T-Wall etc.',
    'Method Statements'
  ];

  const entryCheckpoint = [
    'Security Facilities Design Development',
    'Oil Police Entry Check Point Design',
    'Telecom & Security System Design',
    'Auxiliary Piping & Plumbing works'
  ];

  const dammamWells = [
    'Interface Management Plan',
    'Engineering Procedures such as Interface Management, Pressure Leak Test for Piping System, PWHT etc.',
    'Inspection & Test Plans (ITPs) such as Piping (AG/UG), Electrical Works, Instrumentation, Cathodic Protection, Painting etc.',
    'Method Statements'
  ];

  const preBidEngineering = [
    'Technical Proposal',
    'Project Execution Plans & Strategies',
    'Engineering Deliverable Document Register (EDDR)',
    'Project Schedule',
    'Project Controls Procedures'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/15970023/pexels-photo-15970023.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            West Qurna Phase 2 Development
          </h1>
          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span className="text-lg">Basra, Iraq</span>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              <span className="text-lg">Lukoil</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span className="text-lg">2017-2021</span>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Project <span className="text-red-600">Overview</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mb-8" />
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              One of the world's biggest onshore oilfields. Owned and operated by Lukoil (75%) and North Oil Company (25%). 
              Production reached 100 million tonnes as of 2019.
            </p>
          </div>

          {/* Key Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-indigo-800 text-white">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold mb-2">100M</div>
                <div className="text-red-100">Tonnes Production (2019)</div>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-indigo-800 text-white">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold mb-2">75%</div>
                <div className="text-red-100">Lukoil Ownership</div>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-indigo-800 text-white">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold mb-2">World's</div>
                <div className="text-red-100">Biggest Onshore Oilfields</div>
              </CardContent>
            </Card>
          </div>

          {/* Infrastructure Facilities */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Infrastructure Facilities Engineering Services
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {infrastructureFacilities.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Facilities Design */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Facilities Design & Development
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {facilitiesDesign.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Historical Wells */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Engineering Services for Historical Wells
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {historicalWells.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Technical Writing */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Technical Writing Development & Approval
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {technicalWriting.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Entry Checkpoint */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Entry Checkpoint of Cluster 3 Engineering Services
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {entryCheckpoint.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Dammam Water Injection Wells */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Dammam Water Injection Wells Services
            </h3>
            <p className="text-slate-700 mb-4">
              <strong>3D Model Update:</strong> Using PDMS software for Well Pads #2, #5 & #12 and approval of the same.
            </p>
            <p className="text-slate-700 mb-6">
              <strong>As-Built Documentation:</strong> Preparation of As-Built Drawings & Documents.
            </p>
            <h4 className="text-xl font-bold text-slate-900 mb-4">Technical Writing Development & Approval:</h4>
            <div className="grid md:grid-cols-2 gap-4">
              {dammamWells.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Pre-Bid Engineering */}
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Pre-Bid Engineering for Tender Development - Key Deliverables
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {preBidEngineering.map((item, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{item}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Project Significance */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-6">Project Significance</h3>
            <p className="text-lg text-slate-700 leading-relaxed">
              West Qurna Phase 2 stands as one of the world's most significant onshore oilfield developments. Total Vision's 
              comprehensive involvement across multiple engineering disciplines - from infrastructure facilities and oil wells 
              clusters to security systems, 3D modeling, technical writing, and pre-bid engineering - demonstrates our 
              capability to deliver integrated solutions for mega-scale oil and gas projects. Our contributions to this project, 
              operated by Lukoil in partnership with North Oil Company, have been instrumental in achieving the field's 
              production milestone of 100 million tonnes.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WestQurnaPhase2Page;